import {
  HttpTransportType,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { onAuthenticatedResponse } from '@shared/lib/authenticatedResponse';
import { readTokenDeadlineAt } from '@shared/lib/idleSession';
import { endSession, getNewAlertsNotification, logout } from '../api';
import { SNACKBAR_VARIANTS } from '../constants';
import { enqueueSnackbar, sessionKeptAlive, setUserIdle } from '../slices';
import { HUB_TRIGGER, isTokenFresh, shouldStartHub } from './hubPolicy';

// Session rules for the hub (brief `tasks/prompt-auto-logout-dashboard.md`):
// hub requests never extend the server session, and a (re)connect with an
// expired JWT gets 410, after which the SignalR client gives up on its own
// retry schedule - and a failed INITIAL start is never retried at all. So:
// a dropped hub is never a logout; the hub is stopped while the user is
// idle (no reconnect loop against an expiring JWT); and it is only ever
// started after an authenticated API request succeeded - never on mount,
// in parallel with the first request that renews the cookie - or, on the
// idle flag clearing, while the JWT is known fresh. The same rule brings
// it back after onclose: the next successful request restarts it. See
// hubPolicy.js.
const connection = new HubConnectionBuilder()
  .configureLogging(LogLevel.Debug)
  .withUrl(`${process.env.REACT_APP_API_SIGNALAR_URL}/dashboardHub`, {
    skipNegotiation: true,
    logMessageContent: true,
    transport: HttpTransportType.WebSockets,
  })
  .withAutomaticReconnect()
  .build();

let joinedAccountUuid = null;

const joinGroup = async accountUuid => {
  if (!accountUuid || connection.state !== HubConnectionState.Connected) return;
  await connection.invoke('JoinGroup', accountUuid).catch(error => {
    console.error('SignalR group operation failed:', error);
  });
};

const leaveGroup = async accountUuid => {
  if (!accountUuid || connection.state !== HubConnectionState.Connected) return;
  await connection.invoke('LeaveGroup', accountUuid).catch(error => {
    console.error('SignalR group operation failed:', error);
  });
};

// Groups belong to a connection id, so a reconnect (automatic or ours)
// re-joins the current account's group.
connection.onreconnected(() => {
  joinGroup(joinedAccountUuid);
});

// The automatic reconnects gave up (or the server closed us, e.g. 410 on
// an expired cookie). Nothing to do here: the connection is Disconnected
// now, and the next authenticated API success starts it again.
connection.onclose(error => {
  if (error) console.error('SignalR connection closed:', error);
});

export async function startAlertsConnection() {
  if (connection.state !== HubConnectionState.Disconnected) return;
  try {
    await connection.start();
    await joinGroup(joinedAccountUuid);
  } catch (error) {
    // Not a logout: the next keepalive or interaction starts it again.
    console.error('SignalR connection failed:', error);
  }
}

export async function stopAlertsConnection() {
  if (connection.state === HubConnectionState.Disconnected) return;
  await connection.stop().catch(error => {
    console.error('SignalR stop failed:', error);
  });
}

export default function signalRMiddleware({ dispatch, getState }) {
  const startIf = (trigger, extra = {}) => {
    const { user, appSettings } = getState();
    const start = shouldStartHub({
      trigger,
      signedIn: Boolean(user.isLogin),
      idle: Boolean(appSettings.isUserIdle),
      disconnected: connection.state === HubConnectionState.Disconnected,
      ...extra,
    });
    if (start) startAlertsConnection();
  };

  // Every authenticated 2xx from either fetch layer: the cookie is fresh.
  onAuthenticatedResponse(() => startIf(HUB_TRIGGER.apiSuccess));

  connection.on('ReceiveAlert', () => {
    dispatch(
      enqueueSnackbar({
        message: `You have New Alerts!"`,
        options: {
          variant: SNACKBAR_VARIANTS.info,
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
        },
      })
    );
    setTimeout(() => {
      dispatch(getNewAlertsNotification(getState().user.metadata?.currentAccount?.uuid));
    }, 500);
  });

  return next => async action => {
    if (!action) return;
    switch (action.type) {
      case CHANGE_CURRENT_ACCOUNT_FOR_ALERTS_NOTIFICATION:
        if (action.payload) {
          await leaveGroup(joinedAccountUuid);
          joinedAccountUuid = action.payload;
          await joinGroup(joinedAccountUuid);
          dispatch(getNewAlertsNotification(joinedAccountUuid));
        }
        break;
      case setUserIdle.type:
        if (action.payload) stopAlertsConnection();
        else startIf(HUB_TRIGGER.idleEnd, { tokenFresh: isTokenFresh(readTokenDeadlineAt()) });
        break;
      case sessionKeptAlive.type:
        // The keepalive response already arrived through the client and
        // reported itself as an authenticated response; this only covers
        // the idle flag clearing in the same tick.
        startIf(HUB_TRIGGER.apiSuccess);
        break;
      case logout.fulfilled.type:
      case logout.rejected.type:
      case endSession.fulfilled.type:
        joinedAccountUuid = null;
        stopAlertsConnection();
        break;
      default: {
        break;
      }
    }

    return next(action);
  };
}

const CHANGE_CURRENT_ACCOUNT_FOR_ALERTS_NOTIFICATION =
  'alerts/changeCurrentAccountForAlertsNotifications';

export function changeCurrentAccountForAlertsNotifications(accountUuid) {
  return {
    type: CHANGE_CURRENT_ACCOUNT_FOR_ALERTS_NOTIFICATION,
    payload: accountUuid,
  };
}
